import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import LoadingScreen from '../components/LoadingScreen';

const API_BASE = import.meta.env.VITE_API_BASE;

export default function EmailVerification({ regToken, userEmail, onRegistrationFailed, onForceEmailVerified }) {
  const [error, setError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [initialLoading, setInitialLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [email, setEmail] = useState(userEmail || '');
  const navigate = useNavigate();
  const location = useLocation();
  const timeoutRef = useRef(null);
  const initialCheckDone = useRef(false);

  useEffect(() => {
    if (userEmail) {
      setEmail(userEmail);
    }
  }, [userEmail]);

  // Add an effect to set initial cooldown when component mounts
  useEffect(() => {
    setResendCooldown(10);
    const interval = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const checkVerificationStatus = async () => {
    if (checking) return;
    setChecking(true);
    
    try {
      const response = await fetch(`${API_BASE}/signup/${regToken}/mailv`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      // Handle rate limiting - continue polling silently
      if (response.status === 429) {
        return;
      }

      if (!response.ok) {
        if (response.status === 404) {
          onRegistrationFailed();
          return;
        }
        if (response.status === 401) {
          // Clear all storage and redirect to login
          localStorage.clear();
          sessionStorage.removeItem('registrationToken');
          navigate('/login', { replace: true });
          return;
        }
        const data = await response.json().catch(() => ({}));
        setError(data.message || 'Failed to check verification status');
        return;
      }

      const data = await response.json();
      
      if (data.email) {
        setEmail(data.email);
      }
      
      if (data.verified) {
        // Email is verified, stop polling and redirect
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        window.location.href = `/signup/${regToken}?forceEmailVerified=true`;
        return;
      }
      
      // Continue polling
      timeoutRef.current = setTimeout(checkVerificationStatus, 1000);
    } catch (err) {
      console.error('Failed to check verification status:', err);
      setError('Failed to check verification status. Please try again.');
      // Continue polling even on error
      timeoutRef.current = setTimeout(checkVerificationStatus, 1000);
    } finally {
      setChecking(false);
      setInitialLoading(false);
    }
  };

  // Initial verification check, runs only once when component mounts
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const fromEmail = searchParams.get('from') === 'email';
    const key = searchParams.get('key');
    
    if (!fromEmail || !key) {
      // Start polling only if not coming from email link
      if (initialCheckDone.current) return;
      initialCheckDone.current = true;
      
      // Start verification polling
      checkVerificationStatus();
      
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    } else {
      const verifyEmail = async () => {
        try {
          setChecking(true);
          const verifyRes = await fetch(`${API_BASE}/signup/${regToken}/verifyMail?key=${encodeURIComponent(key)}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json'
            }
          });

          if (!verifyRes.ok) {
            if (verifyRes.status === 404) {
              onRegistrationFailed();
            } else if (verifyRes.status === 401) {
              // Clear all storage and redirect to login
              localStorage.clear();
              sessionStorage.removeItem('registrationToken');
              navigate('/login', { replace: true });
            } else {
              const data = await verifyRes.json().catch(() => ({}));
              setError(data.message || 'Failed to verify email');
            }
          } else {
            const data = await verifyRes.json();
            
            // Always redirect to registration page to show phone verification
            navigate(`/signup/${regToken}`, { replace: true });
          }
        } catch (err) {
          setError('Failed to verify email. Please try again.');
        } finally {
          setChecking(false);
          setInitialLoading(false);
        }
      };

      verifyEmail();
    }
  }, [regToken, location.search, navigate, onRegistrationFailed]);

  const handleGoToPhoneVerification = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    // Use direct location change for more reliable state reset
    window.location.href = `/signup/${regToken}?forceEmailVerified=true`;
  };

  const handleResendEmail = async () => {
    if (resendCooldown > 0) return;

    try {
      setResendCooldown(10);
      const interval = setInterval(() => {
        setResendCooldown(prev => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      const res = await fetch(`${API_BASE}/signup/${regToken}/resendmail`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (res.status === 404) {
        onRegistrationFailed();
        return;
      }
      
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.message || 'Failed to resend verification email');
      }
    } catch (err) {
      setError('Failed to resend verification email');
    }
  };

  const handleUpdateEmail = () => {
    navigate(`/signup/${regToken}/updateemail`);
  };

  return (
    <div className="space-y-4">
      <div className="space-y-6">
        <h2 className="text-2xl font-bold mb-4 text-[var(--text-primary)]">Email Verification</h2>
        
        {error && (
          <div className="mb-8 p-4 rounded bg-[var(--card-bg)] border border-[var(--border-color)] shadow-sm">
            <div className="flex items-center gap-3">
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM11 15H9V13H11V15ZM11 11H9V5H11V11Z" fill="var(--accent-primary)"/>
              </svg>
              <span className="text-[var(--text-primary)] text-sm">{error}</span>
            </div>
          </div>
        )}
        
        <div className="bg-[var(--hover-light)] rounded-lg p-6">
          <div className="flex justify-center mb-4">
            {initialLoading ? (
              <LoadingScreen />
            ) : (
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 4H4C2.9 4 2.01 4.9 2.01 6L2 18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4ZM19.6 8.25L12.53 12.67C12.21 12.87 11.79 12.87 11.47 12.67L4.4 8.25C4.15 8.09 4 7.82 4 7.53C4 6.86 4.73 6.46 5.3 6.81L12 11L18.7 6.81C19.27 6.46 20 6.86 20 7.53C20 7.82 19.85 8.09 19.6 8.25Z" fill="var(--accent-secondary)"/>
              </svg>
            )}
          </div>
          <p className="text-[var(--text-primary)] text-center mb-2 font-medium">
            Check your email inbox
          </p>
          {email && (
            <p className="text-[var(--text-primary)] text-center mb-2 font-medium text-lg">
              {email}
            </p>
          )}

          <div className="text-center mt-6">
            <button
              onClick={handleUpdateEmail}
              className="text-[var(--text-secondary)] text-sm hover:text-[var(--text-primary)] transition-colors"
            >
              Not your email?
            </button>
          </div>
        </div>

        <div className="text-[var(--text-secondary)] text-sm text-center">
          <p>Didn't receive the email?</p>
          <p className="mt-1">
            Check your spam folder or{' '}
            {resendCooldown > 0 ? (
              <span className="text-[var(--text-secondary)] opacity-50">
                Resend Email
              </span>
            ) : (
              <button
                onClick={handleResendEmail}
                className="text-[var(--accent-primary)] hover:text-[var(--text-primary)] hover:underline"
              >
                Resend Email
              </button>
            )}
          </p>
        </div>
      </div>
    </div>
  );
} 